import { useMemo, useState, type FormEvent } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { Banner, Button, TextField } from '@/components/primitives'
import { DetailRow, DetailRows, SummaryCard } from '@/components/cards'
import { AccountPicker } from '@/components/pickers'
import { List, TransactionRow } from '@/components/rows'
import { ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import type { Account } from '@/generated/types'
import { ApiFailure, api } from '@/lib/api'
import { t, tServer } from '@/lib/copy'
import { accountTypeName } from '@/lib/display'
import { formatDate, formatMoney } from '@/lib/format'
import { Link, useNavigate, useRoute } from '@/lib/router'
import { combine, useAsync } from '@/lib/useAsync'
import { markImportPending } from '@/lib/importSignal'

/**
 * Nothing is written until Import is pressed. Renders the locked, warning,
 * needsAccount and saving states from flows.json.
 */
export function ImportReviewScreen() {
  const route = useRoute()
  const navigate = useNavigate()
  const statementId = route.params.statementId

  const statement = useAsync((signal) => api.statements.get(statementId, signal), [statementId])
  const accounts = useAsync((signal) => api.accounts.list(signal), [])

  const locked = statement.state.data?.failureReason === 'passwordProtected'

  /**
   * Asked for at mount, not after the statement comes back and says whether it
   * is locked. Waiting made this a chain of two requests, and through the edge
   * a chain pays the round trip once per link. A password-protected statement
   * answers 422 `passwordProtected` on this route — which is the same fact the
   * statement carries, arriving on its own — so the locked path costs one
   * discarded request and the common path saves a whole round trip.
   */
  const preview = useAsync(
    (signal) =>
      api.statements.preview(statementId, signal).catch((e: unknown) => {
        if (e instanceof ApiFailure && e.body.code === 'passwordProtected') return null
        throw e
      }),
    [statementId],
  )

  const [password, setPassword] = useState('')
  const [unlockError, setUnlockError] = useState<string | null>(null)
  const [unlocking, setUnlocking] = useState(false)

  const [accountId, setAccountId] = useState<string | null>(null)
  const [newAccountName, setNewAccountName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiFailure | null>(null)

  const doc = statement.state.data
  const accountList = accounts.state.data ?? []

  // Default to the account the parser matched, or the only account there is.
  const resolvedAccountId = accountId ?? doc?.accountId ?? (accountList.length === 1 ? accountList[0].id : null)
  const creatingAccount = resolvedAccountId === null

  const suggestedName = useMemo(() => {
    if (!doc?.detected) return ''
    const parts = [doc.detected.institution, doc.detected.mask ? `••${doc.detected.mask}` : null].filter(Boolean)
    return parts.join(' ')
  }, [doc])

  const warnings = useMemo(() => {
    if (!doc) return []
    const out: string[] = []
    if ((doc.skippedRowCount ?? 0) > 0) {
      out.push(t('importReview.warning.skipped', { count: doc.skippedRowCount ?? 0 }))
    }
    if (doc.balanceReconciles === false) out.push(t('importReview.warning.balanceMismatch'))
    if ((doc.duplicateCount ?? 0) > 0) {
      out.push(t('importReview.warning.overlap', { count: doc.duplicateCount ?? 0 }))
    }
    return out
  }, [doc])

  const loaded = combine(statement.state, accounts.state)

  const screenState = saving
    ? 'saving'
    : saveError
      ? 'error'
      : loaded.status === 'loading' && !doc
        ? 'loading'
        : loaded.status === 'error'
          ? 'error'
          : locked
            ? 'locked'
            : creatingAccount && accountList.length > 0
              ? 'needsAccount'
              : warnings.length > 0
                ? 'warning'
                : 'ready'

  const unlock = async (event: FormEvent) => {
    event.preventDefault()
    setUnlocking(true)
    setUnlockError(null)
    try {
      await api.statements.unlock(statementId, password)
      statement.reload()
      preview.reload()
    } catch (e) {
      setUnlockError(e instanceof ApiFailure ? tServer(e.copyKey, undefined, 'importReview.locked.error') : t('importReview.locked.error'))
    } finally {
      setUnlocking(false)
    }
  }

  const confirmImport = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const name = newAccountName.trim() || suggestedName || t('accounts.type.other')
      await api.statements.confirm(
        statementId,
        creatingAccount
          ? {
              accountId: null,
              newAccount: { name, type: (doc?.detected?.accountType ?? 'other') as Account['type'] },
            }
          : { accountId: resolvedAccountId, newAccount: null },
        crypto.randomUUID(),
      )
      markImportPending()
      void api.analysis.refresh().catch(() => undefined)
      navigate('dashboard', {}, { replace: true })
    } catch (e) {
      setSaveError(e instanceof ApiFailure ? e : null)
    } finally {
      setSaving(false)
    }
  }

  const discard = async () => {
    try {
      await api.statements.remove(statementId)
    } finally {
      navigate('upload', {}, { replace: true })
    }
  }

  if (screenState === 'loading') {
    return (
      <Screen id="importReview" state={screenState} back={{ screen: 'upload' }}>
        <LoadingRegion>
          <SkeletonCard />
          <SkeletonList rows={5} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (screenState === 'error' && !doc) {
    return (
      <Screen id="importReview" state={screenState} back={{ screen: 'upload' }}>
        <ErrorState
          titleKey="importReview.error.title"
          error={loaded.status === 'error' ? loaded.error : undefined}
          onRetry={() => {
            statement.reload()
            accounts.reload()
          }}
        />
      </Screen>
    )
  }

  if (screenState === 'locked') {
    return (
      <Screen id="importReview" state={screenState} back={{ screen: 'upload' }}>
        <form className="card card-pad-lg stack" onSubmit={unlock}>
          <h2 className="state-title">{t('importReview.locked.title')}</h2>
          <TextField
            label={t('importReview.locked.field')}
            type="password"
            autoComplete="off"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={unlockError}
          />
          <div className="row">
            <Button variant="primary" type="submit" disabled={unlocking || password.length === 0}>
              {t('importReview.locked.cta')}
            </Button>
            <Button variant="quiet" onClick={() => void discard()}>
              {t('importReview.cta.discard')}
            </Button>
          </div>
        </form>
      </Screen>
    )
  }

  const rows = preview.state.data?.transactions ?? []

  return (
    <Screen id="importReview" state={screenState} back={{ screen: 'upload' }}>
      <div className="stack">
        {warnings.map((warning) => (
          <Banner key={warning} tone="caution">
            {warning}
          </Banner>
        ))}

        {screenState === 'error' ? (
          <ErrorState titleKey="importReview.error.title" error={saveError ?? undefined} onRetry={() => setSaveError(null)} />
        ) : null}

        <SummaryCard>
          <DetailRows>
            <DetailRow label={t('importReview.summary.account')}>
              {doc?.detected?.institution ?? t('accounts.type.other')}
              {doc?.detected?.mask ? ` ••${doc.detected.mask}` : ''}
              {doc?.detected?.accountType ? ` · ${accountTypeName(doc.detected.accountType)}` : ''}
            </DetailRow>
            <DetailRow label={t('importReview.summary.period')}>
              {doc?.periodStart && doc?.periodEnd
                ? t('accountDetail.statement.row', {
                    from: formatDate(doc.periodStart),
                    to: formatDate(doc.periodEnd),
                  })
                : '—'}
            </DetailRow>
            <DetailRow label={t('importReview.summary.transactions')}>{rows.length}</DetailRow>
            <DetailRow label={t('importReview.summary.openingBalance')}>
              {formatMoney(doc?.openingBalance)}
            </DetailRow>
            <DetailRow label={t('importReview.summary.closingBalance')}>
              {formatMoney(doc?.closingBalance)}
            </DetailRow>
          </DetailRows>
        </SummaryCard>

        <div className="card stack">
          {accountList.length > 0 ? (
            <AccountPicker
              accounts={accountList}
              value={resolvedAccountId ?? ''}
              onChange={(next) => setAccountId(next || null)}
              label={t('importReview.account.existing')}
              includeNew
            />
          ) : null}
          {creatingAccount ? (
            <TextField
              label={t('importReview.account.new')}
              placeholder={suggestedName || t('importReview.account.namePlaceholder')}
              value={newAccountName}
              onChange={(e) => setNewAccountName(e.target.value)}
            />
          ) : null}
        </div>

        <section>
          <SectionHeader title={t('importReview.summary.transactions')} />
          {preview.state.status === 'loading' && rows.length === 0 ? (
            <SkeletonList rows={6} />
          ) : (
            <List>
              {rows.map((tx) => (
                <TransactionRow key={tx.id} transaction={tx} />
              ))}
            </List>
          )}
        </section>

        <div className="row">
          <Button variant="primary" onClick={() => void confirmImport()} disabled={saving}>
            {saving ? t('state.saving') : t('importReview.cta.import')}
          </Button>
          <Button variant="quiet" onClick={() => void discard()} disabled={saving}>
            {t('importReview.cta.discard')}
          </Button>
          <Link screen="accounts" className="btn btn-secondary">
            {t('accounts.title')}
          </Link>
        </div>
      </div>
    </Screen>
  )
}
